import { Disclosure, DisclosurePanel } from '@reach/disclosure';
import { Radio } from '@remote-com/mushu/src/components/Ui/Form/Radio/Radio';
import { RadioState } from '@remote-com/mushu/src/components/Ui/Form/Radio/Radio.styled';
import IconArrowRight from '@remote-com/norma/icons/arrow-right.svg';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import { RichText } from 'prismic-reactjs';
import { useState } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { TwoStepFormModalButton } from 'remote-com-shared/components/TwoStepForm';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { breakpointFrom } from 'remote-com-shared/style/mediaQueries';
import styled, { css } from 'styled-components';

import { PricingDialog } from '@/src/domains/pricing/PricingDialog';
import {
  Section,
  BaseCell,
  TitleCell,
  PriceCell,
  ColumnHighlightMessage,
  BillingFrequencyCell,
  FeatureCellContent,
  FeatureCellIcon,
  CTACell,
  FooterCell,
  SimplePriceText,
  ScreamPriceText,
  SpecialPriceStart,
  SpecialPriceBadge,
  SpecialPriceValue,
  SpecialPriceDescription,
  BillingFrequencyControls,
  CTAButton,
  InternationalPricingButton,
} from '@/src/domains/pricing/PricingOverviewSection';
import { desktopBreakpoint, fromDesktopBreakpoint } from '@/src/domains/shared/breakpoints';
import { CTA } from '@/src/domains/shared/forms/ContactUsForm';
import { GlobalPayrollFormModal } from '@/src/views/Platform/GlobalPayrollForm';

export const GET_STARTED_URL = `${process.env.NEXT_PUBLIC_EMPLOY_BASE_URL}/sign-up`;
export const GET_QUOTE_URL = `${process.env.NEXT_PUBLIC_PANDA_BASE_URL}/contact-us`;

export const PricingSection = styled(Section)`
  padding-bottom: 67px;

  ${CTACell} {
    & > a,
    & > ${CTAButton}, & > ${CTA} {
      background-color: var(--newBrandColor);
      box-shadow: none;
    }
  }

  ${({ frenchLocale }) =>
    frenchLocale &&
    css`
      background-color: #ebf2ff;
      background-image: none;
      position: relative;
      z-index: 1;

      ${fromDesktopBreakpoint(css`
        clip-path: ellipse(120% 100% at 50% 0%);
        ${fluidProp('padding-bottom', '150px', '210px')};
      `)}

      ${CTACell} {
        & > a,
        & > ${CTAButton}, & > ${CTA} {
          background-color: #0564ff;
          width: 100%;
        }
      }
    `}
`;

const OPEN_FORM_URL_PARAM_MAP = {
  scheduleDemo: 'schedule_demo_form',
};

function toggleFormIfParam(query, formKey) {
  return !!query[OPEN_FORM_URL_PARAM_MAP[formKey]];
}

const breakpointReference = breakpointFrom.lg;

export const PricingTable = styled.div`
  --cellSpacingX: 22px;
  --cellSpacingY: 16px;
  --edgeSpacing: 50px;
  --cardSpacingX: 24px;
  --cardSpacingY: 32px;
  --intervalSelectorSpacing: 25px;
  --buttonColor: #fff;
  --buttonBackground: var(--colors-irisBlue);
  --darkSeaBlueFont: #003284;

  display: flex;
  flex-direction: column;
  padding-top: 27px;
  gap: 20px;

  ${breakpointReference(css`
    display: grid;
    grid-template-columns: repeat(2, [column] 1fr) [end];
    gap: 20px;
  `)}
`;

export const BillingFrequencyPaddingBottom = styled(BillingFrequencyCell)`
  padding-bottom: 20px;
  padding-top: 0;
`;

const ButtonAdditionalPadding = styled.div`
  padding-bottom: 30px;

  ${desktopBreakpoint(css`
    padding-bottom: 0;
  `)}
`;

const PricingTitleCell = styled(TitleCell)`
  ${fluidProp(['padding-top'], '20px', '34px')}
`;

const PricingPriceCell = styled(PriceCell)`
  padding-bottom: 25px;
`;

const PurpleIcon = styled(FeatureCellIcon)`
  color: var(--colors-irisBlue);
`;

const DescriptionWrapper = styled.p`
  padding-bottom: 20px;
  padding-top: 0;
`;

const DescriptionCell = styled(BaseCell)`
  text-align: center;
  overflow: hidden; // Ensures margins dont open blank spaces

  p {
    margin-top: 0;
    margin-bottom: 30px;
  }

  ${DescriptionWrapper} {
    margin-bottom: 0;
    margin-top: 0;
  }
`;

const PricingButtonWrapper = styled.p`
  margin-top: 0;
  padding-bottom: 10px;

  ${desktopBreakpoint(css`
    margin-bottom: 0;
    padding-bottom: 35px;
  `)}
`;

const FeatureCell = styled(BaseCell)`
  padding-bottom: 19px;

  > *:first-child {
    margin-top: 0;
  }
  > *:last-child {
    margin-bottom: 0;
  }

  ${DescriptionCell} + & {
    padding-top: 15px;
  }

  p {
    margin: 0;
  }
`;

const PricingFeatureCellContent = styled(FeatureCellContent)`
  max-width: none;

  ${breakpointReference(css`
    max-width: 366px;
  `)}
`;

const PricingFeatureCellText = styled.p`
  ${({ $highlight }) =>
    !$highlight &&
    css`
      color: var(--colors-lynch);
    `}
`;

const PricingFooterCell = styled(FooterCell)`
  min-height: 66px;
  padding-bottom: 0;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
`;

export const SpecialPriceWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;

  ${({ $crossed }) =>
    $crossed &&
    css`
      padding-left: 21px;
    `}
`;

export const PricingSpecialPriceBadge = styled(SpecialPriceBadge)`
  display: flex;
  align-items: center;
  margin-left: 2px;
`;

export const PricingSpecialPriceValue = styled(SpecialPriceValue)`
  ${({ $crossed }) =>
    $crossed &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: -8%;
        right: 5%;
        width: 110px;
        height: 3px;
        background-color: var(--colors-turquoise);
        transform: rotate(-12.53deg);
        transform-origin: center;
      }
    `}
`;

const CTACellWithHighlight = styled(CTACell)`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: flex-end;

  ${({ $highlight }) =>
    $highlight &&
    css`
      && > ${CTA} {
        background-color: var(--colors-blank);
        color: #000;

        :hover {
          color: var(--newBrandColor);
        }
      }
    `};
`;

const DisclosurePanelStyled = styled(DisclosurePanel)`
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      display: contents;
    `}
`;

const DisclosureButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 40px;
`;

const DisclosureButton = styled(Button)`
  && {
    background-color: var(--newBrandColor);
    box-shadow: none;
  }
`;
// fr-fr
const IconWrapper = styled.div`
  background-color: #ccdfff;
  width: 24px;
  height: 24px;
  position: absolute;
  left: -8px;
  border-radius: 50px;
`;

const CheckIcon = styled(FeatureCellIcon)`
  color: var(--darkSeaBlueFont);
  top: 4px;
  left: 4px;
`;

export const ColumnHeader = styled.div``;

export const Column = styled.div`
  --seaBlueColor: #0047bc;
  --newDarkBlueFont: #0f3157;

  position: relative;
  border: 1px solid rgba(119, 140, 163, 0.3);

  ${breakpointReference(css`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    box-sizing: border-box;
    border-radius: 5px;

    &:hover {
      background-color: var(--colors-blank);
    }
  `)}

  ${({ $highlight }) =>
    $highlight &&
    css`
      margin-top: -29px;

      ${desktopBreakpoint(css`
        order: -1;
      `)}

      ${BaseCell} {
        --borderColor: #7764e8;
        color: var(--colors-selago);
        background-color: var(--newBrandColor);
      }

      ${PricingTitleCell} {
        color: var(--colors-blank);
        padding-bottom: 18px;
        padding-top: 62px;
      }
    `};

  ${({ $frenchLocale }) =>
    $frenchLocale &&
    css`
      && {
        position: relative;
        background-color: #f5f9ff;
        border-radius: 24px;
        color: var(--seaBlueColor);
        max-width: 542px;
        border: none;

        ${PricingFeatureCellText}, ${InternationalPricingButton} {
          color: var(--seaBlueColor);
        }

        &:hover {
          background-color: var(--colors-blank);
        }

        ${PricingFeatureCellContent} {
          svg {
            display: none;
          }
          ${IconWrapper} {
            svg {
              display: block;
            }
          }
        }

        ${BillingFrequencyControls} {
          justify-content: flex-start;
          --colors-spindle: var(--colors-blank);
        }
        ${InternationalPricingButton} {
          &::after {
            border: none;
          }
        }

        && {
          && {
            ${RadioState} {
              border-color: #317fff;
              background-color: var(--colors-blank);
              --colors-turquoise: #317fff;

              &:before {
                height: 11px;
                width: 11px;
              }
            }
          }
        }

        ${PricingPriceCell} {
          justify-content: flex-start;
          padding-left: 0;
          > * {
            display: flex;
            justify-content: flex-start;
            align-items: baseline;
          }
        }

        ${BillingFrequencyPaddingBottom} {
          padding: 0;
          padding-bottom: 10px;
        }

        ${PricingSpecialPriceValue} {
          color: var(--darkSeaBlueFont);
          font-family: var(--typography-fontBossa);
          font-weight: 500;
          font-size: 24px;
        }

        ${PricingTitleCell} {
          color: var(--darkSeaBlueFont);
          display: flex;
          justify-content: flex-start;
          padding: 0 0 16px;
          font-family: var(--typography-fontBossa);
          ${({ theme }) => theme.typography.titleXL};

          > * {
            display: none;
          }
        }

        ${DisclosureButton} {
          background-color: #ebf2ff;
          color: var(--newDarkBlueFont);
          box-shadow: none;
          width: 90%;
        }

        ${({ $highlight }) =>
          $highlight &&
          css`
            margin-top: 0;
            background-color: var(--colors-blank);

            ${ColumnHeader} {
              background-color: var(--seaBlueColor);
              color: var(--colors-blank);
              border-top-left-radius: 24px;
              border-top-right-radius: 24px;
            }

            ${BaseCell} {
              background-color: transparent;
            }

            ${PricingSpecialPriceValue} , ${SpecialPriceStart},${PricingTitleCell} {
              color: var(--colors-blank);
            }

            ${SpecialPriceStart} {
              margin-right: 4px;
            }

            ${IconWrapper} {
              background-color: #eef9f4;
            }

            ${CheckIcon} {
              color: #089157;
            }
          `};

        ${DescriptionCell} {
          font-size: 14px;
          text-align: left;
          padding: 0;
          display: flex;
          justify-content: flex-start;

          p {
            margin-bottom: 15px;
          }
        }

        ${ColumnHeader} {
          text-align: left;
          padding: 32px;
          margin-bottom: 32px;

          ${fromDesktopBreakpoint(css`
            min-height: 235px;
            width: 100%;
            padding-bottom: 0;
          `)}
        }

        ${({ $openCard }) =>
          !$openCard &&
          css`
            justify-content: space-between;
            background-color: var(--colors-blank);

            ${ColumnHeader} {
              min-height: unset;
            }

            ${PricingTitleCell} , ${DescriptionCell} {
              color: var(--newDarkBlueFont);
              ${({ theme }) => theme.typography.bodySM};
              opacity: 0.65;
              justify-content: center;
            }

            ${SimplePriceText} {
              font-family: var(--typography-fontBossa);
              color: var(--newDarkBlueFont);
            }
          `};
      }
    `};
`;

export function PlanColumn({
  $highlight,
  $turquoiseIcon,
  title,
  price,
  startsOpen,
  billingFrequency,
  description,
  features,
  cta,
  footer,
  frenchLocale,
  learnMoreLabel,
}) {
  const [open, setOpen] = useState(startsOpen);

  const handleOpenCard = () => {
    setOpen(!open);
  };

  return (
    <Column $highlight={$highlight} $frenchLocale={frenchLocale} $openCard={startsOpen}>
      <ColumnHeader>
        {title && <PricingTitleCell>{title}</PricingTitleCell>}
        <PricingPriceCell>{price}</PricingPriceCell>
        <DescriptionCell>{description}</DescriptionCell>
        {billingFrequency && (
          <BillingFrequencyPaddingBottom>{billingFrequency}</BillingFrequencyPaddingBottom>
        )}
      </ColumnHeader>
      <Disclosure open={open}>
        {!open && (
          <DisclosureButtonWrapper>
            <DisclosureButton onClick={handleOpenCard}>
              {learnMoreLabel || 'Learn more'}
            </DisclosureButton>
          </DisclosureButtonWrapper>
        )}
        <DisclosurePanelStyled $isOpen={open}>
          {features.map((feature, index) => (
            <FeatureCell key={index}>
              <PricingFeatureCellContent>
                {frenchLocale && (
                  <IconWrapper>
                    <CheckIcon />
                  </IconWrapper>
                )}
                {!frenchLocale && $turquoiseIcon ? <FeatureCellIcon /> : <PurpleIcon />}
                <PricingFeatureCellText $highlight={$highlight}>{feature}</PricingFeatureCellText>
              </PricingFeatureCellContent>
            </FeatureCell>
          ))}
          {!billingFrequency && (
            <ButtonAdditionalPadding>{billingFrequency}</ButtonAdditionalPadding>
          )}
          <CTACellWithHighlight $highlight={$highlight}>{cta}</CTACellWithHighlight>
          <PricingFooterCell>{footer}</PricingFooterCell>
        </DisclosurePanelStyled>
      </Disclosure>
    </Column>
  );
}

function PricingOverviewSectionPrice({ billingFrequency, ...cost }) {
  const { price_label: priceLabel, highlight_price_label: screamText } = cost;

  if (priceLabel) {
    if (screamText) {
      return <ScreamPriceText>{priceLabel}</ScreamPriceText>;
    }
    return <SimplePriceText>{priceLabel}</SimplePriceText>;
  }

  const amounts = cost.amounts || [];
  const selectedAmount = amounts.find(({ label }) => label === billingFrequency);

  if (!selectedAmount) {
    return null;
  }

  const {
    title,
    amount,
    amount_per_unit: amountPerUnit,
    cross_amount: crossAmount,
    cross_label: crossLabel,
  } = selectedAmount;

  return (
    <div>
      {fieldHasContent(title) && <SpecialPriceStart>{title}</SpecialPriceStart>}
      <SpecialPriceWrapper $crossed={crossAmount}>
        <PricingSpecialPriceBadge aria-describedby="free-price-description">
          {crossLabel}
        </PricingSpecialPriceBadge>
        <PricingSpecialPriceValue $crossed={crossAmount}>{amount}</PricingSpecialPriceValue>
      </SpecialPriceWrapper>
      <SpecialPriceDescription>{amountPerUnit}</SpecialPriceDescription>
    </div>
  );
}

function PricingOverviewSectionBillingFrequency({
  billingFrequency,
  setBillingFrequency,
  ...cost
}) {
  const { amounts } = cost;

  if (!(amounts.length > 0)) {
    return null;
  }

  return (
    <BillingFrequencyControls>
      <Formik
        initialValues={{
          billingFrequency,
        }}
      >
        {({ handleChange }) =>
          amounts.map(({ label, description }, index) => (
            <Radio
              key={index}
              name="billingFrequency"
              value={label}
              label={label}
              description={description || ''}
              onChange={(event) => {
                handleChange(event);
                setBillingFrequency(event.target.value);
              }}
            />
          ))
        }
      </Formik>
    </BillingFrequencyControls>
  );
}

function getPriceAndBilling(cost, [billingFrequency, setBillingFrequency]) {
  const { amounts } = cost || {};

  const hasAmounts = (amounts || []).length > 1;

  return {
    price: <PricingOverviewSectionPrice {...cost} billingFrequency={billingFrequency} />,
    billingFrequency: hasAmounts && (
      <PricingOverviewSectionBillingFrequency
        {...cost}
        billingFrequency={billingFrequency}
        setBillingFrequency={setBillingFrequency}
      />
    ),
  };
}

function PricingPlanColumnCTA({
  openGlobalPayrollModal,
  CustomCTAButton = TwoStepFormModalButton,
  frenchLocale,
  ...plan
}) {
  const { query } = useRouter();

  const {
    primary: {
      call_to_action_button_v1: ctaGetQuote,
      call_to_action_modal_content_v1: ctaModalDemo,
      call_to_action_button_label_v1: ctaLabel,
    },
  } = plan;

  if (ctaGetQuote) {
    return ctaModalDemo ? (
      <CustomCTAButton
        openModal={toggleFormIfParam(query, 'scheduleDemo')}
        IconAfter={frenchLocale && IconArrowRight}
        button={frenchLocale && { as: CTAButton, IconAfter: IconArrowRight }}
      >
        {ctaLabel}
      </CustomCTAButton>
    ) : (
      <CTAButton
        tone="primary"
        shadow
        onClick={openGlobalPayrollModal}
        IconAfter={frenchLocale && IconArrowRight}
      >
        {ctaLabel}
      </CTAButton>
    );
  }

  return (
    <CTAButton
      tone="primary"
      IconAfter={frenchLocale && IconArrowRight}
      shadow
      href={GET_STARTED_URL}
      rel="nofollow"
    >
      {ctaLabel}
    </CTAButton>
  );
}

export function PricingPlanColumn({
  openGlobalPayrollModal,
  CustomCTAButton,
  frenchLocale,
  learnMoreLabel,
  ...plan
}) {
  const {
    primary: { name, description, cost, starts_open: startsOpen },
    label: sliceLabel,
    fields: featuresList,
  } = plan;

  const {
    highlight_message: highlightMessage,
    display_international_pricing: displayInternationalPricing,
    international_pricing_table: internationalPricingTable,
  } = cost || {};
  const amounts = cost?.amounts || [];

  const [billingFrequency, setBillingFrequency] = useState(amounts[0]?.label || '');

  const highlightProps =
    sliceLabel === 'highlight' ? { $highlight: true, $turquoiseIcon: true } : {};

  const pricingAndBilling = getPriceAndBilling(cost, [billingFrequency, setBillingFrequency]);
  const [showInternationalPricing, setInternatinalPricingVisible] = useState(false);

  return (
    <PlanColumn
      frenchLocale={frenchLocale}
      title={
        <>
          {name}
          {highlightMessage && (
            <ColumnHighlightMessage id="free-price-description" aria-hidden="true">
              {RichText.render(highlightMessage)}
            </ColumnHighlightMessage>
          )}
        </>
      }
      {...pricingAndBilling}
      description={RichText.render(description)}
      startsOpen={startsOpen}
      features={featuresList.map(({ feature_name: feature }) => feature)}
      cta={
        <PricingPlanColumnCTA
          openGlobalPayrollModal={openGlobalPayrollModal}
          {...plan}
          CustomCTAButton={CustomCTAButton}
          frenchLocale={frenchLocale}
        />
      }
      footer={
        displayInternationalPricing && (
          <PricingButtonWrapper>
            <InternationalPricingButton onClick={() => setInternatinalPricingVisible(true)}>
              Looking for pricing in your currency?
            </InternationalPricingButton>
            <PricingDialog
              isOpen={showInternationalPricing}
              pricing={internationalPricingTable}
              onDismiss={() => setInternatinalPricingVisible(false)}
            />
          </PricingButtonWrapper>
        )
      }
      learnMoreLabel={learnMoreLabel}
      {...highlightProps}
    />
  );
}

export function PricingOverviewSection({
  pricingOverview,
  hubspotFormDefinition,
  tone,
  CustomCTAButton,
  frenchLocale,
  learnMoreLabel,
}) {
  const [displayGlobalPayrollModal, setDisplayGlobalPayrollModal] = useState(false);

  function openGlobalPayrollModal() {
    setDisplayGlobalPayrollModal(true);
  }
  function closeGlobalPayrollModal() {
    setDisplayGlobalPayrollModal(false);
  }

  const { pricing_overview_section: pricingPlans } = pricingOverview;

  return (
    <PricingSection $tone={tone} id="pricing" as="div" frenchLocale={frenchLocale}>
      <ContentContainer>
        <PricingTable>
          {pricingPlans.map((plan, index) => (
            <PricingPlanColumn
              key={index}
              {...plan}
              openGlobalPayrollModal={openGlobalPayrollModal}
              CustomCTAButton={CustomCTAButton}
              frenchLocale={frenchLocale}
              learnMoreLabel={learnMoreLabel}
            />
          ))}
        </PricingTable>

        <GlobalPayrollFormModal
          hubspotFormDefinition={hubspotFormDefinition}
          visible={displayGlobalPayrollModal}
          onCancel={closeGlobalPayrollModal}
        />
      </ContentContainer>
    </PricingSection>
  );
}
